import type { LiveAiChatScope } from './ai-chat-live-content'
import type { useStartDocumentCollectionAiChatMutation } from '@/graphql/mutations/startDocumentCollectionAiChatMutation.gql.generated'
import type { useStartWorkspaceAiChatMutation } from '@/graphql/mutations/startWorkspaceAiChatMutation.gql.generated'

type StartWorkspaceChat = ReturnType<typeof useStartWorkspaceAiChatMutation>[0]
type StartDocumentCollectionChat = ReturnType<
  typeof useStartDocumentCollectionAiChatMutation
>[0]

export function getGraphqlErrorMessage(error: unknown) {
  if (error instanceof Error && error.message) {
    return error.message
  }

  return 'AI chat is unavailable for this workspace.'
}

async function startWorkspaceSession({
  setCreatedSessionId,
  startWorkspaceChat,
  workspaceId,
}: {
  setCreatedSessionId: (sessionId: string) => void
  startWorkspaceChat: StartWorkspaceChat
  workspaceId: string
}) {
  const result = await startWorkspaceChat({
    variables: { input: { workspaceId } },
  })
  const sessionId = result.data?.startWorkspaceAiChat.id

  if (!sessionId) {
    throw new Error('AI chat could not be started.')
  }

  setCreatedSessionId(sessionId)

  return sessionId
}

async function startDocumentCollectionSession({
  documentCollectionId,
  setCreatedSessionId,
  startDocumentCollectionChat,
  workspaceId,
}: {
  documentCollectionId: string
  setCreatedSessionId: (sessionId: string) => void
  startDocumentCollectionChat: StartDocumentCollectionChat
  workspaceId: string
}) {
  const result = await startDocumentCollectionChat({
    variables: { input: { documentCollectionId, workspaceId } },
  })
  const sessionId = result.data?.startDocumentCollectionAiChat.id

  if (!sessionId) {
    throw new Error('AI chat could not be started.')
  }

  setCreatedSessionId(sessionId)

  return sessionId
}

export async function ensureAiChatSession({
  activeSessionId,
  documentCollectionId,
  scope,
  setCreatedSessionId,
  startDocumentCollectionChat,
  startWorkspaceChat,
  workspaceId,
}: {
  activeSessionId?: string
  documentCollectionId?: string
  scope: LiveAiChatScope
  setCreatedSessionId: (sessionId: string) => void
  startDocumentCollectionChat: StartDocumentCollectionChat
  startWorkspaceChat: StartWorkspaceChat
  workspaceId?: string
}) {
  if (activeSessionId) {
    return activeSessionId
  }

  if (!workspaceId) {
    throw new Error('Select a workspace before starting AI chat.')
  }

  if (scope === 'workspace') {
    return startWorkspaceSession({
      setCreatedSessionId,
      startWorkspaceChat,
      workspaceId,
    })
  }

  if (documentCollectionId) {
    return startDocumentCollectionSession({
      documentCollectionId,
      setCreatedSessionId,
      startDocumentCollectionChat,
      workspaceId,
    })
  }

  throw new Error('AI chat could not be started.')
}
